import React from 'react';
import { useLocation, Link, Navigate } from 'react-router-dom';
import { CaretRight, MapPin, Phone, Clock, ArrowRight, Question } from '@phosphor-icons/react';
import MagereSharedLayout from './MagereSharedLayout';
import SeoHead from '../../components/SeoHead';
import { getSeoLocation } from './components/seoLocations';

const PINK = '#F9055B';
const DARK = '#111111';

const SITE_BASE = typeof window !== 'undefined' ? window.location.origin : 'https://app.mrg-istres.fr';

const LocalSeoPage = () => {
  const location = useLocation();
  const slug = location.pathname.replace(/^\/+|\/+$/g, '');
  const config = getSeoLocation(slug);

  if (!config) {
    return <Navigate to="/" replace />;
  }

  const fullUrl = `${SITE_BASE}/${config.slug}`;
  const isAudio = config.service === 'audioprothesiste';

  // BreadcrumbList Schema.org
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": `${SITE_BASE}/` },
      { "@type": "ListItem", "position": 2, "name": config.h1.replace(' — MRG Optique & Audition', '').replace(' — MRG Optique Istres', '').replace(' — MRG Audition à 15 min', ''), "item": fullUrl },
    ],
  };

  // FAQPage Schema.org
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": config.faq.map(({ q, a }) => ({
      "@type": "Question",
      "name": q,
      "acceptedAnswer": { "@type": "Answer", "text": a },
    })),
  };

  // LocalBusiness (service-area aware)
  const businessLd = {
    "@context": "https://schema.org",
    "@type": isAudio
      ? ["LocalBusiness", "MedicalBusiness", "HealthAndBeautyBusiness"]
      : ["LocalBusiness", "Optician", "HealthAndBeautyBusiness"],
    "@id": `${SITE_BASE}/#${isAudio ? 'audio' : 'optic'}-${config.slug}`,
    "name": "MRG Optique & Audition",
    "alternateName": isAudio ? "MRG Audition" : "MRG Optique",
    "image": `${SITE_BASE}/api/static/branding/mrg-logo.png`,
    "url": fullUrl,
    "telephone": "+33442489861",
    "priceRange": "€€",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Centre Médical, 44 Chemin du Bord de Crau",
      "addressLocality": "Istres",
      "postalCode": "13800",
      "addressCountry": "FR",
    },
    "geo": { "@type": "GeoCoordinates", "latitude": 43.5125, "longitude": 4.9882 },
    "areaServed": [
      { "@type": "City", "name": config.city },
      { "@type": "City", "name": "Istres" },
      { "@type": "City", "name": "Fos-sur-Mer" },
      { "@type": "City", "name": "Miramas" },
      { "@type": "City", "name": "Saint-Mitre-les-Remparts" },
      { "@type": "City", "name": "Port-Saint-Louis-du-Rhône" },
    ],
    "openingHoursSpecification": [
      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"], "opens": "09:00", "closes": "13:00" },
      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"], "opens": "14:00", "closes": "19:00" },
    ],
    "sameAs": ["https://www.facebook.com/people/MRG-Optique/61554091179463/"],
  };

  return (
    <MagereSharedLayout storeSlug="mrg">
      <SeoHead
        title={config.metaTitle}
        description={config.metaDescription}
        url={fullUrl}
        image={`${SITE_BASE}/api/static/branding/mrg-logo.png`}
        jsonLd={[breadcrumbLd, faqLd, businessLd]}
      />

      <div className="bg-white" data-testid={`local-seo-page-${config.slug}`}>
        {/* Breadcrumb visible */}
        <nav aria-label="Fil d'Ariane" className="border-b border-slate-100 bg-slate-50">
          <ol className="max-w-[1200px] mx-auto px-6 py-3 flex items-center gap-2 text-[12px] text-slate-500">
            <li><Link to="/" className="hover:text-[color:var(--pink)] transition-colors" style={{ '--pink': PINK }}>Accueil</Link></li>
            <li><CaretRight size={12} weight="bold" className="text-slate-300" /></li>
            <li className="text-slate-700 font-medium truncate">{config.h1}</li>
          </ol>
        </nav>

        {/* HERO */}
        <section
          className="relative overflow-hidden border-b border-slate-100"
          style={{ background: `linear-gradient(135deg, ${DARK} 0%, #1a1a1a 100%)` }}
          data-testid="local-seo-hero"
        >
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `radial-gradient(circle at 20% 30%, ${PINK} 0%, transparent 40%), radial-gradient(circle at 80% 70%, ${PINK} 0%, transparent 50%)` }} />
          <div className="max-w-[1200px] mx-auto px-6 py-20 md:py-28 relative">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.18em] font-semibold mb-6"
                style={{ background: `${PINK}22`, color: PINK, border: `1px solid ${PINK}55` }}>
                <MapPin size={14} weight="fill" /> {config.city} · {config.cityPostalCode}
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {config.h1}
              </h1>
              <p className="text-lg text-white/70 leading-relaxed mb-8 max-w-2xl">
                {config.headline}. {config.heroIntro}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/rendez-vous" data-testid="local-seo-cta-rdv"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-semibold text-white transition-all hover:scale-[1.02]"
                  style={{ background: PINK }}>
                  Prendre rendez-vous <ArrowRight size={18} weight="bold" />
                </Link>
                <a href="tel:0442489861"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-semibold text-white border border-white/20 hover:bg-white/10 transition-all">
                  <Phone size={18} weight="fill" /> 04 42 48 98 61
                </a>
              </div>
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-[13px] text-white/60">
                <span className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full" style={{ background: PINK }} />+30 grandes marques</span>
                <span className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full" style={{ background: PINK }} />Tiers payant mutuelles</span>
                <span className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full" style={{ background: PINK }} />Conseils d'experts</span>
              </div>
            </div>
          </div>
        </section>

        {/* SECTIONS DE CONTENU */}
        <section className="max-w-[1200px] mx-auto px-6 py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-16">
            {config.sections.map((s, i) => (
              <div key={i} className="group" data-testid={`local-seo-section-${i}`}>
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold mb-3" style={{ color: PINK }}>
                  0{i + 1}
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 leading-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {s.title}
                </h2>
                <p className="text-slate-600 leading-relaxed mb-5">{s.body}</p>
                {s.cta && (
                  <Link to={s.cta.href} className="inline-flex items-center gap-2 text-sm font-semibold border-b-2 pb-0.5 transition-all hover:gap-3"
                    style={{ color: DARK, borderColor: PINK }}>
                    {s.cta.label} <ArrowRight size={16} weight="bold" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* INFOS PRATIQUES */}
        <section className="bg-slate-50 border-y border-slate-100">
          <div className="max-w-[1200px] mx-auto px-6 py-16 grid md:grid-cols-3 gap-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: PINK }}>
                <MapPin size={22} weight="fill" className="text-white" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 mb-1">Adresse</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Centre Médical, 44 Chemin du Bord de Crau<br />13800 Istres</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: PINK }}>
                <Phone size={22} weight="fill" className="text-white" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 mb-1">Téléphone</h3>
                <a href="tel:0442489861" className="text-sm text-slate-600 hover:underline">04 42 48 98 61</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: PINK }}>
                <Clock size={22} weight="fill" className="text-white" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 mb-1">Horaires</h3>
                <p className="text-sm text-slate-600 leading-relaxed">Lun–Sam : 9h–13h<br />/ 14h–19h</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-[900px] mx-auto px-6 py-16 md:py-24" data-testid="local-seo-faq">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.18em] font-semibold mb-4"
              style={{ background: `${PINK}11`, color: PINK }}>
              <Question size={14} weight="fill" /> Questions fréquentes
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Vous vous demandez…
            </h2>
          </div>
          <div className="space-y-3">
            {config.faq.map((item, i) => (
              <details key={i} className="group border border-slate-200 rounded-2xl p-5 open:border-[color:var(--pink)] open:bg-slate-50 transition-colors"
                style={{ '--pink': PINK }} data-testid={`faq-item-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer list-none font-semibold text-slate-900">
                  <span className="pr-4">{item.q}</span>
                  <span className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-transform group-open:rotate-45 text-white font-bold"
                    style={{ background: PINK }}>+</span>
                </summary>
                <p className="text-sm text-slate-600 leading-relaxed mt-4">{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* MAILLAGE INTERNE */}
        {config.nearbyLinks && config.nearbyLinks.length > 0 && (
          <section className="bg-slate-900 text-white">
            <div className="max-w-[1200px] mx-auto px-6 py-16">
              <h2 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Vous pourriez aussi chercher
              </h2>
              <p className="text-white/60 mb-8 text-sm">Autres services et villes desservies par MRG Optique & Audition.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {config.nearbyLinks.map((link, i) => (
                  <Link key={i} to={link.href}
                    className="group p-5 rounded-xl border border-white/10 hover:border-[color:var(--pink)] hover:bg-white/5 transition-all"
                    style={{ '--pink': PINK }}
                    data-testid={`nearby-link-${i}`}>
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-medium text-sm">{link.label}</span>
                      <ArrowRight size={16} weight="bold" className="opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" style={{ color: PINK }} />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </MagereSharedLayout>
  );
};

export default LocalSeoPage;
